/// <reference path="../globals.d.ts" />

import axios, { AxiosInstance, AxiosError } from 'axios'
import qs from 'qs'

import asPromise from '../util/as-promise'
import SessionManager from '../lib/session_manager'

type RequestParams = { [key: string]: any } | undefined

type ApiClientOptions = { skipAuth?: boolean, debug?: boolean }

declare global {
  interface Window {
    pendingRequests: number
  }
}

export const ERROR_MAP = {
  401: 'ERRORS.API.INVALID_CURRENT_PASSWORD',
  403: 'ERRORS.API.UNAUTHORIZED',
  404: 'ERRORS.API.NOT_FOUND',
  409: 'ERRORS.API.CONFLICT',
  422: 'ERRORS.API.INVALID_BODY',
  500: 'ERRORS.API.INTERNAL',
  504: 'ERRORS.API.TIMEOUT'
}

export class ApiCallError extends Error {
  public messageKey: string

  constructor (
    public message: string,
    public status?: number,
    public errorData?: Object,
    public axiosError?: AxiosError
  ) {
    super(message)
    this.messageKey = ApiCallError.translateStatus(status)
  }

  static translateStatus (status) {
    return ERROR_MAP[status] || 'ERRORS.API.UNKNOWN'
  }

}

class ApiClient {

  private restClient: AxiosInstance
  private path: string = ''

  constructor (private basePath?: string, options: ApiClientOptions = {}, private readonly windowLocation = window.location) {
    this.restClient = axios.create({
      baseURL: `${API_HOST}/${basePath ? basePath : ''}`,
      headers: {
        'content-type': 'application/json',
        'Client-App': SessionManager.Instance.isManager() ? '72c8cd0a-1f43-4fe5-8bac-e3b5e7eb723f' : '05a98bad-a7ac-4cfa-a140-d33e38e42da4'
      }
    })
    if (!options.skipAuth) {
      this.restClient.defaults.headers['Auth-Token'] = SessionManager.Instance.getAuthToken()
    }
    // istanbul ignore else: NODE_ENV is replaced at build time, the specs always run with 'test'
    if (NODE_ENV === 'test') this.injectCounter()
    /* istanbul ignore next */
    if (options.debug) this.injectLogging()
  }

  resetAuthToken () {
    this.restClient.defaults.headers['Auth-Token'] = SessionManager.Instance.getAuthToken()
  }

  get<T> (path = '', params?: RequestParams, data?: Object): Promise<T> {
    this.path = path
    return asPromise<T>(
      this.restClient.get(this.buildPath(path, params))
        .catch(this.handleError.bind(this))
    )
  }

  post<T> (path = '', data: Object, params?: RequestParams): Promise<T> {
    this.path = path
    return asPromise<T>(
      this.restClient.post(this.buildPath(path, params), data)
        .catch(this.handleError.bind(this))
    )
  }

  put<T> (path = '', data: Object, params?: RequestParams): Promise<T> {
    this.path = path
    return asPromise<T>(
      this.restClient.put(this.buildPath(path, params), data)
        .catch(this.handleError.bind(this))
    )
  }

  delete<T> (path = '', data?: Object, params?: RequestParams): Promise<T> {
    this.path = path
    return asPromise<T>(
      this.restClient.delete(this.buildPath(path, params), data)
        .catch(this.handleError.bind(this))
    )
  }

  scope (path) {
    return new ApiClient(`${this.basePath}/${path}`)
  }

  private handleError (error: AxiosError) {
    if (this.path !== 'sign_in' && !this.path.includes('current_password')) {
      if (error.response && error.response.status === 401) {
        this.windowLocation.reload.call(this.windowLocation)
      }
      if (error.response && error.response.status === 403) {
        SessionManager.Instance.logout()
        if (this.windowLocation.pathname !== '/') {
          this.windowLocation.reload.call(this.windowLocation)
        }
      }
    }

    return Promise.reject(new ApiCallError(error.message, error.response && error.response.status, error.response!.data, error))
  }

  private buildPath (path, params: RequestParams) {
    return `${path}${params ? `?${qs.stringify(params, { arrayFormat: 'brackets' })}` : ''}`
  }

  /* istanbul ignore next */
  private injectLogging () {
    this.restClient.interceptors.request.use(request => {
      console.log('Starting Request', request)
      return request
    })
    this.restClient.interceptors.response.use(response => {
      console.log('Got Response', response)
      return response
    })
  }

  private injectCounter () {
    window.pendingRequests = window.pendingRequests || 0
    this.restClient.interceptors.request.use(request => {
      window.pendingRequests++
      return request
    }, (error) => {
      window.pendingRequests--
      return Promise.reject(error)
    })
    this.restClient.interceptors.response.use(response => {
      window.pendingRequests--
      return response
    }, (error) => {
      window.pendingRequests--
      return Promise.reject(error)
    })
  }
}

export default ApiClient
